import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PricingCard } from '../components/pricing/PricingCard';
import { ComparisonTable } from '../components/pricing/ComparisonTable';
export function WebDesign() {
  const [tableExpanded, setTableExpanded] = useState(false);
  const toggleTable = () => {
    setTableExpanded(!tableExpanded);
  };
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBronzeClick = () => {
    window.open('https://buy.stripe.com/aFa14hfmQ5ga8v85gsaZi00', '_blank');
  };
  const handleSilverClick = () => {
    window.open('https://buy.stripe.com/eVqcMZ3E8cICbHk4coaZi01', '_blank');
  };
  const handleGoldClick = () => {
    window.open('https://buy.stripe.com/eVq3cpfmQ6ke8v86kwaZi02', '_blank');
  };
  return <>
      <Helmet data-id="element-2269">
        <title data-id="element-2270">Website Design Packages UK | Bespoke Sites from £399</title>
        <meta name="description" content="Responsive, SEO-ready websites built in the UK. Up to 15 pages, branding and content options." data-id="element-2271" />
        <link rel="canonical" href="https://bright-pixxel.com/web-design" data-id="element-2272" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2273" />
        <meta property="og:type" content="website" data-id="element-2274" />
        <meta property="og:title" content="Website Design Packages UK | Bespoke Sites from £399" data-id="element-2275" />
        <meta property="og:description" content="Responsive, SEO-ready websites built in the UK. Up to 15 pages, branding and content options." data-id="element-2276" />
        <meta property="og:url" content="https://bright-pixxel.com/web-design" data-id="element-2277" />
        <meta property="og:site_name" content="Bright Pixxel Studios" data-id="element-2278" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2279" />
        <meta property="og:image:width" content="1200" data-id="element-2280" />
        <meta property="og:image:height" content="630" data-id="element-2281" />
        <meta property="og:image:type" content="image/png" data-id="element-2282" />
        <meta property="og:image:alt" content="Bright Pixxel Studios - Professional Web Design Services" data-id="element-2283" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2284" />
        <meta name="twitter:title" content="Website Design Packages UK | Bespoke Sites from £399" data-id="element-2285" />
        <meta name="twitter:description" content="Responsive, SEO-ready websites built in the UK. Up to 15 pages, branding and content options." data-id="element-2286" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2287" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studios - Professional Web Design Services" data-id="element-2288" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-[clamp(56px,8vw,72px)] pb-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2289">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2290">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2291">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2292">
              Web Design Packages from £399 Inc VAT
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2293">
              Professional, mobile-optimised websites that deliver exceptional
              user experiences. Every site we create is built with performance,
              accessibility, and SEO best practices at its core.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Pricing Cards Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2294">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2295">
          <div className="max-w-7xl mx-auto" data-id="element-2296">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2297">
              <PricingCard title="Bronze" subtitle="Starter" price="£399" popular={false} features={['Up to 5 pages', 'Responsive design', 'Enquiry form', 'Blog option', 'Basic SEO', '1 business email']} iconSrc="https://images.unsplash.com/photo-1605478328994-f93e98217da7?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Bronze web design plan icon" ctaText="Get Started" onClick={handleBronzeClick} data-id="element-2298" />
              <PricingCard title="Silver" subtitle="Growing Business" price="£599" popular={true} features={['Up to 10 pages', 'Branding + favicon', '3 business emails', 'SEO setup', 'Domain + SSL (year 1)', '30-day support']} iconSrc="https://images.unsplash.com/photo-1568480289356-5a75d0fd47fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Silver web design plan icon" ctaText="Most Popular" onClick={handleSilverClick} data-id="element-2299" />
              <PricingCard title="Gold" subtitle="Full Service" price="£899" popular={false} features={['Up to 15 pages', 'Logo + trust badges', 'Advanced SEO', 'Payment links', 'Content writing included', 'GA + GSC setup']} iconSrc="https://images.unsplash.com/photo-1610375461369-d613b564f4c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Gold web design plan icon" ctaText="Get Started" onClick={handleGoldClick} data-id="element-2300" />
            </div>
            <div className="mt-12 text-center" data-id="element-2301">
              <button onClick={toggleTable} className="inline-flex items-center justify-center mx-auto px-8 py-3 rounded-full font-medium text-[15px] text-[#0DC3DC] bg-transparent border border-[#0DC3DC] border-opacity-40 hover:bg-gradient-to-r hover:from-[#0DC3DC] hover:to-[#00A8BF] hover:text-white hover:border-opacity-0 transition-all duration-300 shadow-sm hover:shadow-lg" style={{
              fontFamily: 'Poppins, sans-serif',
              minHeight: '46px'
            }} data-id="element-2302">
                <span className="mr-2" data-id="element-2303">
                  {tableExpanded ? 'Hide detailed comparison' : 'Show detailed comparison'}
                </span>
                {tableExpanded ? <ChevronUpIcon size={16} data-id="element-2304" /> : <ChevronDownIcon size={16} data-id="element-2305" />}
              </button>
            </div>
            {/* Comparison Table */}
            {tableExpanded && <ComparisonTable data-id="element-2306" />}
          </div>
        </div>
      </section>
      {/* Portfolio Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2307">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2308">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2309">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2310">
              Our Recent Projects
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2311">
              Take a look at some of our recent web design projects. We've
              helped businesses across various industries establish a strong
              online presence and achieve their goals.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2312">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2313">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pQKeoA3T9GzSKJQNGTdgoz/websites_1_-_ooOhmy.co.uk.jpg" alt="ooOhMy.co.uk e-commerce website - professional ladies fashion online store designed by Bright Pixxel Studios with modern layout and seamless shopping experience" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2314" />
              <div className="p-4 flex-grow" data-id="element-2315">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2316">ooOhMy.co.uk</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2317">
                  E-commerce / Ladies Fashion
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2318">
                  "Bright Pixxel transformed our online store with a beautiful,
                  user-friendly design that has increased our sales by 45%.
                  Their attention to detail and e-commerce expertise made all
                  the difference."
                </p>
                <a href="https://ooohmy.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2319">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2320">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d5GzTyf2oC8ykFDj3X9wmi/Website_3_-_northamptondrivingschool.co.uk.jpg" alt="Northampton Driving School website - professional driving school website with online booking system designed by Bright Pixxel Studios UK" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2321" />
              <div className="p-4 flex-grow" data-id="element-2322">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2323">
                  Northampton Driving School
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2324">
                  Education / Driving Lessons
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2325">
                  "Bookings increased by 60% since launching our new website.
                  The online booking system has streamlined our operations and
                  the mobile-friendly design has been perfect for our younger
                  students."
                </p>
                <a href="https://northamptondrivingschool.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2326">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2327">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4kYo3C4gd9QjfCrgk7a12h/Website_2_-_medicolegal-psychiatry.co.uk.jpg" alt="Medicolegal Psychiatry website - professional healthcare psychiatry practice website designed by Bright Pixxel Studios with patient-focused design" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2328" />
              <div className="p-4 flex-grow" data-id="element-2329">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2330">
                  Medicolegal Partners
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2331">
                  Healthcare / Psychiatry
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2332">
                  "The professional site perfectly represents our practice.
                  Bright Pixxel understood our need for a sophisticated,
                  trustworthy online presence that respects patient
                  confidentiality while being informative."
                </p>
                <a href="https://medicolegal-psychiatry.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2333">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2334">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2mYs5H4cmXngmP57UGoyEE/Website_4_-_surefix.it.com.jpg" alt="SureFix.it appliance repair website - professional home appliance repair service website with online booking designed by Bright Pixxel Studios" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2335" />
              <div className="p-4 flex-grow" data-id="element-2336">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2337">SureFix.it</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2338">
                  Home Services / Appliance Repair
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2339">
                  "Lead generation increased 75% year-on-year after our website
                  redesign. The online booking system and service area map have
                  dramatically improved customer experience and our conversion
                  rate."
                </p>
                <a href="https://surefix.it.com" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2340">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2341">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wqw4YWP28GPXqhAPXrrgAj/Website_5_-_axiscare.it.com.jpg" alt="AxisCare appliance repair website - professional appliance repair service website with SEO optimization designed by Bright Pixxel Studios UK" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2342" />
              <div className="p-4 flex-grow" data-id="element-2343">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2344">AxisCare.it</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2345">
                  Home Services / Appliance Repair
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2346">
                  "ROI on our website in just 2 months! The SEO work has put us
                  on the first page of Google for our key service areas. Bright
                  Pixxel delivered exactly what we needed to grow our repair
                  business."
                </p>
                <a href="https://axiscare.it.com" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2347">
                  Visit website →
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2348">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2349">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2350">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mb-8" data-id="element-2351">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio - Affordable, Creative, Digital Excellence" className="h-16 mx-auto" data-id="element-2352" />
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2353">
              Need a custom package?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2354">
              We understand that every business is unique. If our standard
              packages don't quite fit your needs, we'd be happy to create a
              custom solution tailored specifically for you.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-2355">
              <Button size="lg" onClick={scrollToContact} data-id="element-2356">
                Contact Us Today
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}